"use client"

import { useState } from "react"
import { Star, Clock, HeadphonesIcon, BadgeCheck, ChevronUp, ChevronDown, Loader2, Upload, Trash } from "lucide-react";
import {Badge} from "../../../components/ui/badge";
import {Button} from "../../../components/ui/button";
import BulkUploadModal from "../../../components/BulkUploadModal";
import {useBulkUpload, suppliersBulkConfig} from "../../../configuration/bulk";
import CreateSupplierButton from "../Buttons/CreateSupplierButton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "../../../components/ui/dialog";

export default function SuppliersTable({
  suppliers,
  onSupplierClick,
  onSupplierDeleted,
  isLoading,
  clientId,
  onSupplierCreated
}) {
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [deletingSupplierId, setDeletingSupplierId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    supplier: null
  });

  const {isUploading, uploadResult, handleBulkUpload} = useBulkUpload(
    clientId,
    suppliersBulkConfig
  );

  // Helper function to safely format rating values
  const formatRating = (rating) => {
    const numRating = Number(rating) || 0;
    return numRating.toFixed(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Change direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New sort field
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Handle supplier deletion
  const handleDeleteSupplier = async (supplier, event) => {
    event.stopPropagation(); // Prevent row click

    // Open the confirmation dialog
    setDeleteDialog({
      open: true,
      supplier: supplier
    });
  };

  // Confirm and execute supplier deletion
  const confirmDeleteSupplier = async () => {
    const supplier = deleteDialog.supplier;
    if (!supplier) return;

    try {
      setDeletingSupplierId(supplier.id);
      const {deleteSupplier} = await import(
        "../../../../ApiCalls/mantisFlowsSuppliers"
      );
      const response = await deleteSupplier(clientId, supplier.id);

      if (response && response.success) {
        onSupplierDeleted?.(supplier.id);
      } else {
        alert("Error al eliminar el proveedor");
      }
    } catch (error) {
      console.error("Error al eliminar proveedor:", error);
      alert("Error al eliminar el proveedor");
    } finally {
      setDeletingSupplierId(null);
      setDeleteDialog({open: false, supplier: null});
    }
  };

  // Sort suppliers
  const sortedSuppliers = [...(suppliers || [])].sort((a, b) => {
    let comparison = 0;

    switch (sortField) {
      case "name":
        comparison = a.name.localeCompare(b.name);
        break;
      case "country":
        comparison = a.country.localeCompare(b.country);
        break;
      case "rating_overall":
        comparison = (a.rating_overall || 0) - (b.rating_overall || 0);
        break;
      default:
        comparison = 0;
    }

    return sortDirection === "asc" ? comparison : -comparison;
  });

  return (
    <div className="space-y-4 px-4 py-3">
      <div className="flex justify-between">
        <CreateSupplierButton
          clientId={clientId}
          onSupplierCreated={onSupplierCreated}
          size="default"
          variant="default"
          showIcon={true}
          showText={true}
        />
        <Button
          onClick={() => setIsBulkUploadOpen(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white"
        >
          <Upload className="h-4 w-4 mr-2" />
          Carga Masiva
        </Button>
      </div>

      <div className="overflow-hidden border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm bg-white dark:bg-slate-800">
        {/* Table header */}
        <div className="grid grid-cols-12 gap-4 p-6 font-medium text-sm text-slate-700 dark:text-slate-300 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80">
          <div
            className="col-span-3 flex items-center cursor-pointer hover:text-orange-500 transition-colors"
            onClick={() => handleSort("name")}
          >
            Proveedor
            {sortField === "name" &&
              (sortDirection === "asc" ? (
                <ChevronUp className="h-4 w-4 ml-1" />
              ) : (
                <ChevronDown className="h-4 w-4 ml-1" />
              ))}
          </div>
          <div
            className="col-span-2 flex items-center cursor-pointer hover:text-orange-500 transition-colors"
            onClick={() => handleSort("country")}
          >
            País
            {sortField === "country" &&
              (sortDirection === "asc" ? (
                <ChevronUp className="h-4 w-4 ml-1" />
              ) : (
                <ChevronDown className="h-4 w-4 ml-1" />
              ))}
          </div>
          <div className="col-span-3">Contacto</div>
          <div
            className="col-span-3 flex items-center cursor-pointer hover:text-orange-500 transition-colors"
            onClick={() => handleSort("rating_overall")}
          >
            Calificación
            {sortField === "rating_overall" &&
              (sortDirection === "asc" ? (
                <ChevronUp className="h-4 w-4 ml-1" />
              ) : (
                <ChevronDown className="h-4 w-4 ml-1" />
              ))}
          </div>
          <div className="col-span-1 text-center">Acciones</div>
        </div>

        {/* Table rows */}
        <div className="divide-y divide-slate-200 dark:divide-slate-700">
          {isLoading ? (
            <div className="p-12 text-center text-slate-500 dark:text-slate-400 flex flex-col items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-orange-500 mb-2" />
              <span>Cargando proveedores...</span>
            </div>
          ) : sortedSuppliers.length === 0 ? (
            <div className="p-12 text-center text-slate-500 dark:text-slate-400">
              No se encontraron proveedores con los filtros actuales
            </div>
          ) : (
            sortedSuppliers.map((supplier) => (
              <div
                key={supplier.id}
                className="grid grid-cols-12 gap-4 p-6 hover:bg-slate-50 dark:hover:bg-slate-800/60 cursor-pointer transition-colors"
                onClick={() => onSupplierClick(supplier)}
              >
                <div className="col-span-3">
                  <div className="font-medium text-slate-800 dark:text-slate-200">
                    {supplier.name}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-1">
                    {supplier.description || "Sin descripción"}
                  </div>
                </div>
                <div className="col-span-2 flex items-center">
                  <Badge
                    variant="outline"
                    className="bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                  >
                    {supplier.country}
                  </Badge>
                </div>
                <div className="col-span-3">
                  <div className="text-slate-700 dark:text-slate-300 text-sm">
                    {supplier.contact_email}
                  </div>
                  {supplier.phone && (
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      {supplier.phone}
                    </div>
                  )}
                </div>
                <div className="col-span-3">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex items-center text-amber-500">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`h-3.5 w-3.5 ${
                            i < Math.round(Number(supplier.rating_overall) || 0)
                              ? "fill-amber-500"
                              : "fill-transparent"
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {formatRating(supplier.rating_overall)}
                    </span>
                    {supplier.is_favorite && (
                      <Star className="h-4 w-4 fill-amber-500 text-amber-500 ml-2" />
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div
                      className="flex items-center bg-blue-50 dark:bg-blue-900/20 px-1.5 py-0.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                      title="Calificación de Servicio"
                    >
                      <HeadphonesIcon className="h-3 w-3 text-blue-500 dark:text-blue-400 mr-0.5" />
                      <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                        {formatRating(supplier.rating_service)}
                      </span>
                    </div>

                    <div
                      className="flex items-center bg-green-50 dark:bg-green-900/20 px-1.5 py-0.5 rounded hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                      title="Calificación de Puntualidad"
                    >
                      <Clock className="h-3 w-3 text-green-500 dark:text-green-400 mr-0.5" />
                      <span className="text-xs font-medium text-green-700 dark:text-green-300">
                        {formatRating(supplier.rating_punctuality)}
                      </span>
                    </div>

                    <div
                      className="flex items-center bg-purple-50 dark:bg-purple-900/20 px-1.5 py-0.5 rounded hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                      title="Calificación de Calidad"
                    >
                      <BadgeCheck className="h-3 w-3 text-purple-500 dark:text-purple-400 mr-0.5" />
                      <span className="text-xs font-medium text-purple-700 dark:text-purple-300">
                        {formatRating(supplier.rating_quality)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="col-span-1 flex items-center justify-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => handleDeleteSupplier(supplier, e)}
                    disabled={deletingSupplierId === supplier.id}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    {deletingSupplierId === supplier.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <BulkUploadModal
        isOpen={isBulkUploadOpen}
        onClose={() => setIsBulkUploadOpen(false)}
        onUpload={handleBulkUpload}
        isUploading={isUploading}
        title="Carga Masiva de Proveedores"
        config={suppliersBulkConfig}
        uploadResult={uploadResult}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({...deleteDialog, open})}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-red-600">
              Confirmar Eliminación de Proveedor
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="text-center">
              <Trash className="h-12 w-12 text-red-500 mx-auto mb-3" />
              <p className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                ¿Estás seguro de que quieres eliminar el proveedor "
                {deleteDialog.supplier?.name}"?
              </p>

              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mt-4">
                <div className="flex items-center gap-2 text-red-700 dark:text-red-300 mb-2">
                  <Trash className="h-4 w-4" />
                  <span className="font-semibold">¡Advertencia!</span>
                </div>
                <p className="text-sm text-red-600 dark:text-red-400">
                  Esta acción eliminará permanentemente el proveedor y no se
                  puede deshacer.
                </p>
              </div>

              <p className="text-sm text-slate-600 dark:text-slate-400 mt-4">
                Esta acción no se puede deshacer.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({...deleteDialog, open: false})}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteSupplier}
              disabled={deletingSupplierId === deleteDialog.supplier?.id}
            >
              {deletingSupplierId === deleteDialog.supplier?.id ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash className="h-4 w-4 mr-2" />
                  Eliminar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
